import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const TEAM = { solo: "Just me", "2_5": "2–5", "6_15": "6–15", "16_plus": "16+" };
const SOURCE = { diag: "Free Business Diag", approach: "Approach page", direct: "Direct link" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

function useAdminList(path) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(path).then((r) => setRows(r.data)).catch((e) => {
      setRows([]);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || `Failed to load ${path}`);
    });
  }, [path]);
  return rows;
}

function Table({ testId, head, rows, empty }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-cvc-border bg-white" data-testid={testId}>
      <table className="w-full font-body text-sm">
        <thead className="bg-cvc-parchment text-cvc-ink-soft text-xs uppercase tracking-wider">
          <tr>{head.map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={head.length} className="px-4 py-8 text-center text-cvc-ink-soft">{empty}</td></tr>}
          {rows}
        </tbody>
      </table>
    </div>
  );
}

function Person({ r }) {
  return <td className="px-4 py-3"><span className="font-medium text-cvc-ink">{r.business_name || r.name}</span><br /><span className="text-xs text-cvc-ink-soft">{r.name} · {r.email} · {r.phone}</span></td>;
}

/** Admin view of FREE BUSINESS DIAG consult requests and Business Scaling Assessment leads. */
export const DiagAssessmentPanel = () => {
  const bookings = useAdminList("/admin/next-move/bookings");
  const leads = useAdminList("/admin/assessment-leads");
  if (!bookings || !leads) return <p className="font-body text-cvc-ink-soft p-6">Loading…</p>;
  return (
    <div className="space-y-10">
      <section>
        <h2 className="font-heading text-2xl text-cvc-navy">Consult requests</h2>
        <p className="font-body text-sm text-cvc-ink-soft mt-1 mb-4">From the diag results and the assessment scorecard. Full briefs arrive by email.</p>
        <Table testId="admin-diag-bookings" head={["Requested", "Owner", "From", "Preferred time", "Assessment"]} empty="No consult requests yet."
          rows={bookings.map((b) => (
            <tr key={b.id} data-testid={`admin-diag-booking-${b.id}`} className="border-t border-cvc-border">
              <td className="px-4 py-3 text-cvc-ink-soft whitespace-nowrap">{fmt(b.created_at)}</td>
              <Person r={b} />
              <td className="px-4 py-3">{b.source === "assessment" ? "Assessment" : "Diag"} · {b.channel}</td>
              <td className="px-4 py-3 text-cvc-ink-soft">{b.preferred_time}</td>
              <td className="px-4 py-3">{b.assessment_result ? `${b.assessment_result.overall_pct}% · ${b.assessment_result.verdict}` : "—"}</td>
            </tr>
          ))} />
      </section>
      <section>
        <h2 className="font-heading text-2xl text-cvc-navy">Business Scaling Assessment</h2>
        <p className="font-body text-sm text-cvc-ink-soft mt-1 mb-4">Everyone who gave contact details to take the assessment.</p>
        <Table testId="admin-assessment-leads" head={["Started", "Owner", "Came from", "Team", "Score", "Weakest areas", "Booked"]} empty="No assessment leads yet."
          rows={leads.map((l) => (
            <tr key={l.id} data-testid={`admin-assessment-lead-${l.id}`} className="border-t border-cvc-border">
              <td className="px-4 py-3 text-cvc-ink-soft whitespace-nowrap">{fmt(l.created_at)}</td>
              <Person r={l} />
              <td className="px-4 py-3">{SOURCE[l.source] || "Free Business Diag"}</td>
              <td className="px-4 py-3">{TEAM[l.team_size] || "—"}</td>
              <td className="px-4 py-3 whitespace-nowrap">{l.assessment_result ? `${l.assessment_result.overall_pct}% · ${l.assessment_result.verdict}` : "Not finished"}</td>
              <td className="px-4 py-3 text-cvc-ink-soft">{(l.assessment_result?.weakest || []).map((w) => w.title).join(", ") || "—"}</td>
              <td className="px-4 py-3">{l.booked_at ? "Yes" : l.follow_up_sent_at ? "Invite sent" : "Not yet"}</td>
            </tr>
          ))} />
      </section>
    </div>
  );
};
